import React, { useState, useEffect } from 'react';
import api from '../api';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Button,
  Stack,
  Container,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Divider,
  Chip
} from '@mui/material';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function DashboardPage() {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    // Default to previous month (n-1) since current month data isn't complete
    const prevMonth = now.getMonth(); // getMonth() is 0-indexed, so current month - 1
    const year = prevMonth === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = prevMonth === 0 ? 12 : prevMonth;
    return { year, month };
  });

  useEffect(() => {
    fetchBreakdown();
  }, [selectedMonth]);

  const fetchBreakdown = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/api/analytics/breakdown', {
        params: {
          year: selectedMonth.year,
          month: selectedMonth.month
        }
      });

      setBreakdown(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleMonthChange = (direction) => {
    setSelectedMonth(prev => {
      let newMonth = prev.month + direction;
      let newYear = prev.year;

      if (newMonth > 12) {
        newMonth = 1;
        newYear++;
      } else if (newMonth < 1) {
        newMonth = 12;
        newYear--;
      }

      return { year: newYear, month: newMonth };
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getMonthName = (month) => {
    const date = new Date(2000, month - 1, 1);
    return date.toLocaleString('default', { month: 'long' });
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  const MonthSelector = () => (
    <Stack direction="row" alignItems="center" spacing={1}
      sx={{ bgcolor: 'background.paper', px: 2, py: 1, borderRadius: 1.5, boxShadow: '0 2px 4px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
      <Button
        onClick={() => handleMonthChange(-1)}
        sx={{ minWidth: 36, color: '#6366f1', fontSize: '1.2rem', p: 0.5 }}
      >
        ←
      </Button>
      <Typography variant="body1" fontWeight={600} sx={{ minWidth: 150, textAlign: 'center' }}>
        {getMonthName(selectedMonth.month)} {selectedMonth.year}
      </Typography>
      <Button
        onClick={() => handleMonthChange(1)}
        sx={{ minWidth: 36, color: '#6366f1', fontSize: '1.2rem', p: 0.5 }}
      >
        →
      </Button>
    </Stack>
  );

  if (!breakdown || breakdown.breakdown.transactionCount === 0) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Monthly Spending Dashboard
            </Typography>
            <MonthSelector />
          </Stack>
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2, borderColor: 'divider', border: '1px solid' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No transactions found for {getMonthName(selectedMonth.month)} {selectedMonth.year}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try navigating to a different month or upload a statement to get started!
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Monthly Spending Dashboard
          </Typography>
          <MonthSelector />
        </Stack>

        {/* Summary Cards */}
        <Grid container spacing={2} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Total Spending
                </Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {formatCurrency(breakdown.summary.totalSpend)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Transactions
                </Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {breakdown.summary.transactionCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Average Transaction
                </Typography>
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  {formatCurrency(breakdown.summary.averageTransaction)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Top Category
                </Typography>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                  {breakdown.summary.topCategory.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatCurrency(breakdown.summary.topCategory.amount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <Typography variant="h6" fontWeight={600} color="text.primary" mb={2}>
                Spending by Category
              </Typography>
              <Box sx={{ position: 'relative', height: { xs: 280, md: 400 } }}>
                <Pie data={breakdown.charts.pie} options={{ maintainAspectRatio: false }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <Typography variant="h6" fontWeight={600} color="text.primary" mb={2}>
                Category Breakdown
              </Typography>
              <Box sx={{ position: 'relative', height: { xs: 280, md: 400 } }}>
                <Bar data={breakdown.charts.bar} options={{ ...breakdown.charts.bar.options, maintainAspectRatio: false }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Category Details Table */}
        <Paper sx={{ borderRadius: 2, boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              Category Details
            </Typography>
          </Box>
          <Divider />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40, color: 'text.secondary', fontWeight: 600 }}></TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Percentage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(breakdown.breakdown.categoryBreakdown)
                  .filter(([category]) => category !== 'Payments')
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, amount]) => (
                    <React.Fragment key={category}>
                      <TableRow
                        onClick={() => toggleCategory(category)}
                        sx={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <TableCell sx={{ color: '#6366f1', fontSize: '0.8rem', textAlign: 'center' }}>
                          {expandedCategories[category] ? '▼' : '▶'}
                        </TableCell>
                        <TableCell>{category}</TableCell>
                        <TableCell>{formatCurrency(amount)}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${breakdown.breakdown.categoryPercentages[category].toFixed(1)}%`}
                            size="small"
                            sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#818cf8', fontWeight: 600, fontSize: '0.75rem' }}
                          />
                        </TableCell>
                      </TableRow>
                      {expandedCategories[category] && breakdown.topTransactionsByCategory[category] && (
                        <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                          <TableCell colSpan={4} sx={{ p: 0, borderBottom: '2px solid', borderColor: 'divider' }}>
                            <Box sx={{ m: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ width: 100, color: 'text.secondary', fontWeight: 600, fontSize: '0.82rem' }}>Date</TableCell>
                                    <TableCell sx={{ width: 200, color: 'text.secondary', fontWeight: 600, fontSize: '0.82rem' }}>Merchant</TableCell>
                                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.82rem' }}>Description</TableCell>
                                    <TableCell sx={{ width: 100, color: 'text.secondary', fontWeight: 600, fontSize: '0.82rem', textAlign: 'right' }}>Amount</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {breakdown.topTransactionsByCategory[category].map((txn, idx) => (
                                    <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                      <TableCell sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem', verticalAlign: 'top' }}>
                                        {new Date(txn.date).toLocaleDateString()}
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.85rem', verticalAlign: 'top' }}>
                                        {txn.merchant}
                                      </TableCell>
                                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem', lineHeight: 1.4, verticalAlign: 'top' }}>
                                        {txn.description}
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 600, color: 'error.main', fontSize: '0.85rem', textAlign: 'right', verticalAlign: 'top' }}>
                                        {formatCurrency(txn.amount)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  );
}

export default DashboardPage;
